'use client'

import * as React from 'react'
import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { cn } from 'lib/utils'

function Avatar({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root>) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn(
        'relative flex size-8 shrink-0 overflow-hidden rounded-full',
        className,
      )}
      {...props}
    />
  )
}

function AvatarImage({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn('aspect-square size-full', className)}
      {...props}
    />
  )
}

function AvatarFallback({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback>) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        'bg-muted flex size-full items-center justify-center rounded-full',
        className,
      )}
      {...props}
    />
  )
}

// Genera un colore deterministico basato su una stringa
function stringToColor(str: string): string {
  const colors = [
    '#6366f1', // indigo
    '#8b5cf6', // violet
    '#a855f7', // purple
    '#d946ef', // fuchsia
    '#ec4899', // pink
    '#f43f5e', // rose
    '#ef4444', // red
    '#f97316', // orange
    '#eab308', // yellow
    '#84cc16', // lime
    '#22c55e', // green
    '#14b8a6', // teal
    '#06b6d4', // cyan
    '#0ea5e9', // sky
    '#3b82f6', // blue
  ]

  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  return colors[Math.abs(hash) % colors.length]
}

// Genera le iniziali da nome e cognome
function getInitials(firstName?: string, lastName?: string, fallback?: string): string {
  const first = firstName?.trim().charAt(0).toUpperCase() || ''
  const last = lastName?.trim().charAt(0).toUpperCase() || ''

  if (first && last) return `${first}${last}`
  if (first) return first
  if (last) return last
  if (fallback) return fallback.trim().charAt(0).toUpperCase()
  return '?'
}

interface UserAvatarProps {
  firstName?: string
  lastName?: string
  username?: string
  imageUrl?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  showOnlineIndicator?: boolean
  isOnline?: boolean
}

const sizeClasses = {
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-12 h-12 text-base',
}

function UserAvatar({
  firstName,
  lastName,
  username,
  imageUrl,
  size = 'md',
  className,
  showOnlineIndicator = false,
  isOnline = false,
}: UserAvatarProps) {
  const initials = getInitials(firstName, lastName, username)
  const displayName = `${firstName || ''} ${lastName || ''}`.trim() || username || 'User'
  const bgColor = stringToColor(displayName)

  // Verifica se l'immagine è valida (non placeholder o path rotti)
  const hasValidImage = imageUrl &&
    !imageUrl.includes('placeholder') &&
    !imageUrl.startsWith('/placeholder')

  return (
    <div className={cn('relative inline-block', className)}>
      <Avatar className={cn(sizeClasses[size])}>
        {hasValidImage ? (
          <AvatarImage src={imageUrl} alt={displayName} />
        ) : null}
        <AvatarFallback
          className="font-semibold text-white"
          style={{ backgroundColor: bgColor }}
        >
          {initials}
        </AvatarFallback>
      </Avatar>

      {showOnlineIndicator && (
        <div
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-2',
            size === 'sm' ? 'w-2 h-2' : 'w-2.5 h-2.5'
          )}
          style={{
            backgroundColor: isOnline ? 'var(--hexa-success)' : 'var(--hexa-text-muted)',
            borderColor: 'var(--hexa-bg-gradient-start)',
          }}
        />
      )}
    </div>
  )
}

export { Avatar, AvatarImage, AvatarFallback, UserAvatar, getInitials, stringToColor }
